import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import {
  TrendingUp, AlertTriangle, Target, Loader2, RefreshCw,
  AlertCircle, CheckCircle2, Info, ChevronRight, FileText, ExternalLink,
} from 'lucide-react';
import { getTenderWinRate, getTenderRedFlags, getSimilarTenders } from '../../lib/api';
import { useNavigate } from 'react-router-dom';

const CATEGORY_STYLES = {
  HIGH: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  MODERATE: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  LOW: 'bg-red-500/15 text-red-400 border-red-500/30',
  VERY_LOW: 'bg-red-600/15 text-red-500 border-red-600/30',
};

const SEVERITY_COLORS = {
  high: 'text-red-400',
  medium: 'text-amber-400',
  low: 'text-blue-400',
};

function WinRateCard({ data, loading, onRefresh }) {
  if (!data || loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Win Rate Prediction
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Obliczam...
            </div>
          ) : (
            <Button onClick={onRefresh} size="sm">
              <TrendingUp className="w-4 h-4 mr-2" /> Oblicz predykcję
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Win Rate Prediction
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="text-3xl font-bold">{data.win_rate_score}/100</div>
            <Badge variant="outline" className={CATEGORY_STYLES[data.category]}>
              {data.category} · {data.recommendation}
            </Badge>
          </div>
          <div className="flex-shrink-0">
            <Progress value={data.win_rate_score} className="w-24 h-2" />
          </div>
        </div>

        <div className="text-xs text-muted-foreground">Model: {data.method}</div>

        <div className="pt-2 border-t space-y-1.5">
          {data.factors?.map((f, i) => (
            <div key={i} className="flex items-start justify-between text-xs">
              <div className="flex-1">
                <span className="font-medium">{f.name}</span>
                <div className="text-muted-foreground line-clamp-1">{f.note}</div>
              </div>
              <Badge
                variant="outline"
                className={
                  f.impact > 0 ? 'text-emerald-400 border-emerald-500/30' :
                  f.impact < 0 ? 'text-red-400 border-red-500/30' : ''
                }
              >
                {f.impact > 0 ? '+' : ''}{f.impact}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function RedFlagsCard({ data, loading, onRefresh }) {
  if (!data || loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Red Flag Detector
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Analizuję...
            </div>
          ) : (
            <Button onClick={onRefresh} size="sm">
              <AlertTriangle className="w-4 h-4 mr-2" /> Wykryj red flags
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const risk = data.risk_score || 0;
  const riskCategory =
    risk >= 80 ? 'VERY_LOW' :
    risk >= 60 ? 'LOW' :
    risk >= 40 ? 'MODERATE' : 'HIGH';  // inverse: high risk = low win rate

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Red Flag Detector
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="text-3xl font-bold">{risk}/100</div>
            <div className="text-xs text-muted-foreground">
              Risk score · rekomendacja: <Badge variant="outline" className="ml-1">{data.recommendation}</Badge>
            </div>
          </div>
          <div className="flex-shrink-0">
            <Progress value={risk} className="w-24 h-2" />
          </div>
        </div>

        {data.consilium_used && (
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Info className="w-3 h-3" /> Łączone z Consilium AI (risk: {data.consilium_risk})
          </div>
        )}

        <div className="space-y-2 pt-2 border-t">
          {(data.flags || []).slice(0, 8).map((f, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <AlertCircle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${SEVERITY_COLORS[f.severity] || 'text-muted-foreground'}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{f.category}</Badge>
                  <span className={`text-xs ${SEVERITY_COLORS[f.severity]}`}>−{f.score_impact}</span>
                  {f.citation?.pdf_link && (
                    <a
                      href={f.citation.pdf_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      title={f.citation.attachment_filename || 'Otwórz PDF'}
                    >
                      <FileText className="w-3 h-3" />
                      Strona {f.citation.page_no}
                      <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
                <div className="text-muted-foreground mt-0.5">{f.description}</div>
                {f.citation?.excerpt && (
                  <div className="mt-1 p-2 rounded bg-muted/30 text-[11px] italic text-muted-foreground border-l-2 border-primary/40">
                    „{f.citation.excerpt}"
                  </div>
                )}
              </div>
            </div>
          ))}
          {(!data.flags || data.flags.length === 0) && (
            <div className="flex items-center gap-2 text-sm text-emerald-400">
              <CheckCircle2 className="w-4 h-4" />
              Brak wykrytych red flags
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SimilarTendersCard({ data, loading, onRefresh }) {
  const navigate = useNavigate();

  if (!data || loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="w-4 h-4" /> Podobne przetargi
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Szukam...
            </div>
          ) : (
            <Button onClick={onRefresh} size="sm">
              <Target className="w-4 h-4 mr-2" /> Znajdź podobne
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="w-4 h-4" /> Podobne przetargi (FAISS)
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {data.similar_tenders?.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Brak podobnych przetargów w indexie. Dodaj więcej SWZ do FAISS aby rozszerzyć wyszukiwanie.
          </p>
        ) : (
          <div className="space-y-2">
            {(data.similar_tenders || []).slice(0, 8).map((t, i) => (
              <button
                key={i}
                onClick={() => navigate(`/tenders/${encodeURIComponent(t.tender_id)}`)}
                className="w-full text-left p-2 rounded-md hover:bg-muted transition flex items-center gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm line-clamp-1">{t.title}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{t.source}</Badge>
                    {t.region && <span>{t.region}</span>}
                    {t.estimated_value > 0 && (
                      <span>{Number(t.estimated_value).toLocaleString('pl-PL')} PLN</span>
                    )}
                  </div>
                </div>
                <Badge variant="outline" className="text-xs shrink-0">
                  {(t.similarity_score * 100).toFixed(0)}%
                </Badge>
                <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
        {data.note && (
          <p className="text-xs text-muted-foreground mt-3 pt-2 border-t">{data.note}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function PredictionPanel({ tenderId }) {
  const [winRate, setWinRate] = useState(null);
  const [redFlags, setRedFlags] = useState(null);
  const [similar, setSimilar] = useState(null);
  const [loadingWR, setLoadingWR] = useState(false);
  const [loadingRF, setLoadingRF] = useState(false);
  const [loadingSim, setLoadingSim] = useState(false);

  const fetchWinRate = async () => {
    setLoadingWR(true);
    try {
      const r = await getTenderWinRate(tenderId);
      setWinRate(r);
    } catch (e) {
      toast.error('Błąd Win Rate: ' + (e.response?.data?.detail || e.message));
    } finally { setLoadingWR(false); }
  };

  const fetchRedFlags = async () => {
    setLoadingRF(true);
    try {
      const r = await getTenderRedFlags(tenderId);
      setRedFlags(r);
    } catch (e) {
      toast.error('Błąd Red Flags: ' + (e.response?.data?.detail || e.message));
    } finally { setLoadingRF(false); }
  };

  const fetchSimilar = async () => {
    setLoadingSim(true);
    try {
      const r = await getSimilarTenders(tenderId, 10);
      setSimilar(r);
    } catch (e) {
      toast.error('Błąd Similar: ' + (e.response?.data?.detail || e.message));
    } finally { setLoadingSim(false); }
  };

  // Auto-fetch on mount
  useEffect(() => {
    fetchWinRate();
    fetchRedFlags();
    fetchSimilar();
    // eslint-disable-next-line
  }, [tenderId]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <WinRateCard data={winRate} loading={loadingWR} onRefresh={fetchWinRate} />
      <RedFlagsCard data={redFlags} loading={loadingRF} onRefresh={fetchRedFlags} />
      <div className="md:col-span-2">
        <SimilarTendersCard data={similar} loading={loadingSim} onRefresh={fetchSimilar} />
      </div>
    </div>
  );
}
